import clsx from 'clsx';
import { useState, useRef, useEffect, ReactNode } from 'react';

type SwitchProps = {
    checked?: boolean;
    disabled?: boolean;
    renderThumb?: (isChecked: boolean) => ReactNode;
    onToggle?: () => void;
};

const Switch: React.FC<SwitchProps> = ({ checked, disabled, renderThumb, onToggle }) => {
    const [isChecked, setIsChecked] = useState(checked ?? false);
    const [translateDistance, setTranslateDistance] = useState(0);

    const labelRef = useRef<HTMLLabelElement>(null);
    const divRef = useRef<HTMLDivElement>(null);

    const toggle = () => {
        if (disabled) return;
        setIsChecked((prev) => !prev);
        onToggle && onToggle();
    };

    const handleEnterKeydown = (e: React.KeyboardEvent) => {
        e.preventDefault();

        if (e.key === 'Enter') {
            toggle();
        }
    };

    useEffect(() => {
        if (!labelRef.current || !divRef.current) return;

        const labelWidth = labelRef.current.getBoundingClientRect().width;
        const spanWidth = divRef.current.getBoundingClientRect().width;

        setTranslateDistance(labelWidth - spanWidth * 1.5);
    }, [labelRef, divRef]);

    return (
        <label
            className={clsx('switch', { disabled })}
            tabIndex={disabled ? -1 : 0}
            ref={labelRef}
            onKeyUp={handleEnterKeydown}
            aria-disabled={disabled}
        >
            <input
                type='checkbox'
                name='switch'
                checked={checked ?? isChecked}
                onChange={toggle}
                tabIndex={-1}
            />
            <div
                className='thumb'
                style={{
                    transform: `translateX(${isChecked ? translateDistance : 0}px)`,
                }}
                ref={divRef}
            >
                {renderThumb && renderThumb(isChecked)}
            </div>
        </label>
    );
};

export default Switch;
